import { CartCountResponse, ErrorResponse, ServiceResponse } from '@bzbs/react-api-client';
import React, { createContext, useContext, useState } from 'react';
import { createCartUrl } from '../service/bzbs_service';
import { useBuzzebeesServiceContext } from './buzzebees_service_provider';

type CartContextType = {
  count: number;
  isLoading: boolean;
  error: ErrorResponse | null;
  actions: {
    getCartUrl: (returnUrl: string) => Promise<string | null>;
    addToCart: (id: string, mode: string, qty: number, data?: string) => Promise<ServiceResponse<CartCountResponse>>;
    getCount: () => Promise<ServiceResponse<CartCountResponse>>;
  };
};

const CartContext = createContext({
  count: 0,
  isLoading: false,
  error: null,
  actions: {
    getCartUrl: async () => '',
    addToCart: async () => ({ type: 'client-error', message: 'Not implemented' }),
    getCount: async () => ({ type: 'client-error', message: 'Not implemented' }),
  },
} as CartContextType);

export function useCartContext() {
  return useContext(CartContext);
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ErrorResponse | null>(null);

  const { bzbsService, appId, appName, urls } = useBuzzebeesServiceContext();

  const getCartUrl = async (returnUrl: string): Promise<string | null> => {
    setIsLoading(true);
    const response = await bzbsService.cartApi.cartAccess({
      errorUrl: returnUrl,
      successUrl: returnUrl,
      returnUrl: returnUrl,
      appId: appId,
      appName: appName,
    });
    setIsLoading(false);

    if (response.type === 'success') {
      const accessKey = response.model.data?.key ?? '';
      const result = createCartUrl(urls.cart, appName, accessKey);
      return result;
    }

    return null;
  };

  const addToCart = async (
    id: string,
    mode: string,
    qty: number,
    data?: string
  ): Promise<ServiceResponse<CartCountResponse>> => {
    setIsLoading(true);
    const response = await bzbsService.cartApi.addCart({
      id: id,
      mode: mode,
      qty: qty,
      sideCampaignJson: data,
    });
    setIsLoading(false);

    if (response.type === 'success') {
      setCount(response.model.cart_count ?? 0);
      setError(null);
    } else {
      setError(response);
    }

    return response;
  };

  const getCount = async (): Promise<ServiceResponse<CartCountResponse>> => {
    setIsLoading(true);
    const response = await bzbsService.cartApi.cartCount();
    setIsLoading(false);

    if (response.type === 'success') {
      setCount(response.model.cart_count ?? 0);
      setError(null);
    } else {
      setError(response);
    }

    return response;
  };

  return (
    <CartContext.Provider
      value={{
        count: count,
        isLoading: isLoading,
        error: error,
        actions: {
          getCartUrl,
          addToCart,
          getCount,
        },
      }}
    >
      {children}
    </CartContext.Provider>
  );
}
